import { useEffect, useState } from "react";

/**
 * Custom hook to manage training metrics, quality checks, and analysis.
 *
 * Handles:
 * - Training statistics
 * - Quality checks
 * - Dataset analysis
 * - Training-output metric parsing
 * - Time formatting
 */
export const useTrainingMetrics = () => {
  const [trainingStats, setTrainingStats] = useState({
    modelHealth: {
      status: "No data",
      risk: "Unknown",
      gap: "N/A",
      badgeColor: "bg-gray-100 text-gray-600",
    },
    epochs: {
      count: 0,
      intensity: "Not started",
      batchSize: 0,
      badgeColor: "bg-gray-100 text-gray-600",
    },
    lastRun: {
      time: "Never",
      status: "Idle",
      pipeline: "v1.0",
      badgeColor: "bg-gray-100 text-gray-600",
    },
    dataVolume: {
      rows: 0,
      quality: "Unknown",
      features: 0,
      badgeColor: "bg-gray-100 text-gray-600",
    },
  });

  const [qualityChecks, setQualityChecks] = useState({
    featureScaling: {
      status: "PENDING",
      message: "Not yet analyzed",
    },
    classBalance: {
      status: "PENDING",
      message: "Not yet analyzed",
    },
    missingValues: {
      status: "PENDING",
      message: "Not yet analyzed",
    },
    dataLeakage: {
      status: "PENDING",
      message: "Not yet analyzed",
    },
    trainValSplit: {
      status: "PENDING",
      message: "Not yet analyzed",
    },
  });

  const [lastRunTime, setLastRunTime] = useState(null);
  const [showQualityChecks, setShowQualityChecks] = useState(true);

  const getTimeAgo = (timestamp) => {
    if (!timestamp) {
      return "Never";
    }

    const seconds = Math.floor(
      (Date.now() - timestamp) / 1000
    );

    if (seconds < 60) {
      return `${seconds} seconds ago`;
    }

    if (seconds < 3600) {
      return `${Math.floor(seconds / 60)} minutes ago`;
    }

    if (seconds < 86400) {
      return `${Math.floor(seconds / 3600)} hours ago`;
    }

    return `${Math.floor(seconds / 86400)} days ago`;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (lastRunTime) {
        setTrainingStats((previousStats) => ({
          ...previousStats,
          lastRun: {
            ...previousStats.lastRun,
            time: getTimeAgo(lastRunTime),
          },
        }));
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [lastRunTime]);

  const analyzeDataQuality = (code, output) => {
    const checks = {
      featureScaling: {
        status: "PENDING",
        message: "Not analyzed",
      },
      classBalance: {
        status: "PENDING",
        message: "Not analyzed",
      },
      missingValues: {
        status: "PENDING",
        message: "Not analyzed",
      },
      dataLeakage: {
        status: "PENDING",
        message: "Not analyzed",
      },
      trainValSplit: {
        status: "PENDING",
        message: "Not analyzed",
      },
    };

    const fullCode = Object.values(code)
      .join("\n")
      .toLowerCase();

    const outputLower = String(output || "").toLowerCase();

    const hasScaling =
      /standardscaler|minmaxscaler|normalizer|robust|scale|scaler|preprocessing\.scale/.test(
        fullCode
      ) ||
      /scaled|normalized|standardized/.test(outputLower);

    checks.featureScaling = hasScaling
      ? {
          status: "PASS",
          message: "Feature scaling detected",
        }
      : {
          status: "WARNING",
          message:
            "No scaling detected - consider StandardScaler or MinMaxScaler",
        };

    const hasBalanceCheck =
      /class_weight|smote|imblearn|balance|stratified/.test(
        fullCode
      ) ||
      /class distribution|imbalanced|balanced/.test(
        outputLower
      );

    const mentionsClassificationMetrics =
      /accuracy|precision|recall|f1/.test(fullCode);

    checks.classBalance = hasBalanceCheck
      ? {
          status: "PASS",
          message: "Class balance handling detected",
        }
      : mentionsClassificationMetrics
        ? {
            status: "WARNING",
            message: "Consider checking class distribution",
          }
        : {
            status: "PASS",
            message: "Class balance check applied",
          };

    const hasMissingHandling =
      /dropna|fillna|isnull|isna|missing|imputer|simpleimputer/.test(
        fullCode
      );

    const hasMissingInOutput =
      /nan|missing|null values/.test(outputLower);

    checks.missingValues =
      hasMissingHandling || !hasMissingInOutput
        ? {
            status: "PASS",
            message: "Missing values handled",
          }
        : {
            status: "FAIL",
            message:
              "Missing values detected - use dropna() or fillna()",
          };

    const hasProperSplit = /train_test_split/.test(fullCode);
    const fitsOnTest = /fit.*test|transform.*before.*split/.test(
      fullCode
    );
    const hasFitTransform = /fit_transform/.test(fullCode);

    checks.dataLeakage =
      !fitsOnTest && (!hasFitTransform || hasProperSplit)
        ? {
            status: "PASS",
            message: "No obvious data leakage detected",
          }
        : {
            status: "WARNING",
            message:
              "Possible leakage - ensure scaling happens after train/test split",
          };

    const hasSplit =
      /train_test_split|kfold|cross_val|validation_split/.test(
        fullCode
      );

    const hasTestSize =
      /test_size|validation|val/.test(fullCode);

    checks.trainValSplit =
      hasSplit || hasTestSize
        ? {
            status: "PASS",
            message: "Train/validation split detected",
          }
        : {
            status: "FAIL",
            message:
              "No train/test split found - use train_test_split()",
          };

    setQualityChecks(checks);
  };

  const normalizeMetric = (rawValue) => {
    if (rawValue == null) {
      return null;
    }

    const textValue = String(rawValue).trim();

    if (!textValue) {
      return null;
    }

    const numericValue = Number.parseFloat(
      textValue.replace("%", "")
    );

    if (!Number.isFinite(numericValue)) {
      return null;
    }

    if (textValue.includes("%")) {
      return numericValue / 100;
    }

    if (numericValue > 1 && numericValue <= 100) {
      return numericValue / 100;
    }

    if (numericValue < 0 || numericValue > 1) {
      return null;
    }

    return numericValue;
  };

  const getLastMetricValue = (matches) => {
    if (!matches || matches.length === 0) {
      return null;
    }

    const finalMatch = matches[matches.length - 1];

    return normalizeMetric(finalMatch[1]);
  };

  const parseTrainingMetrics = (output) => {
    try {
      if (!output || typeof output !== "string") {
        return {
          epochs: 0,
          batchSize: 32,
          trainAccuracy: null,
          valAccuracy: null,
          f1Score: null,
        };
      }

      const epochMatches = [
        ...output.matchAll(
          /Epoch\s+(\d+)\s*\/\s*(\d+)/gi
        ),
      ];

      const totalEpochs =
        epochMatches.length > 0
          ? Number.parseInt(
              epochMatches[epochMatches.length - 1][2],
              10
            )
          : 0;

      const batchMatch = output.match(
        /batch[_\s-]*size\s*[:=]?\s*(\d+)/i
      );

      const batchSize = batchMatch
        ? Number.parseInt(batchMatch[1], 10)
        : 32;

      const valAccuracyMatches = [
        ...output.matchAll(
          /(?:val_accuracy|validation_accuracy|validation accuracy|val accuracy)\s*[:=]?\s*(0?\.\d+|1(?:\.0+)?|\d+(?:\.\d+)?%)/gi
        ),
      ];

      const trainAccuracyMatches = [
        ...output.matchAll(
          /(?:train_accuracy|training_accuracy|train accuracy|training accuracy)\s*[:=]?\s*(0?\.\d+|1(?:\.0+)?|\d+(?:\.\d+)?%)/gi
        ),
      ];

      /*
       * Keras normally prints:
       * accuracy: 0.9214 - val_accuracy: 0.8875
       *
       * Use the generic accuracy expression only when a clearly labelled
       * train accuracy value was not found.
       */
      let resolvedTrainAccuracyMatches = trainAccuracyMatches;

      if (resolvedTrainAccuracyMatches.length === 0) {
        const genericAccuracyMatches = [
          ...output.matchAll(
            /(?:^|[\s-])accuracy\s*[:=]?\s*(0?\.\d+|1(?:\.0+)?|\d+(?:\.\d+)?%)/gi
          ),
        ];

        resolvedTrainAccuracyMatches = genericAccuracyMatches;
      }

      const f1Matches = [
        ...output.matchAll(
          /(?:macro[_\s-]*|micro[_\s-]*|weighted[_\s-]*)?f1(?:[_\s-]*score)?\s*[:=]?\s*(0?\.\d+|1(?:\.0+)?|\d+(?:\.\d+)?%)/gi
        ),
      ];

      return {
        epochs: totalEpochs,
        batchSize,
        trainAccuracy: getLastMetricValue(
          resolvedTrainAccuracyMatches
        ),
        valAccuracy: getLastMetricValue(
          valAccuracyMatches
        ),
        f1Score: getLastMetricValue(f1Matches),
      };
    } catch (error) {
      console.error(
        "Error parsing training metrics:",
        error
      );

      return {
        epochs: 0,
        batchSize: 32,
        trainAccuracy: null,
        valAccuracy: null,
        f1Score: null,
      };
    }
  };

  const analyzeDataset = (fileContent) => {
    if (!fileContent) {
      return {
        rows: 0,
        features: 0,
      };
    }

    try {
      const lines = fileContent.trim().split("\n");
      const rows = Math.max(0, lines.length - 1);

      if (lines.length > 0) {
        const headers = lines[0].split(",");

        return {
          rows,
          features: headers.length,
        };
      }

      return {
        rows: 0,
        features: 0,
      };
    } catch (error) {
      console.error("Error analyzing dataset:", error);

      return {
        rows: 0,
        features: 0,
      };
    }
  };

  const calculateOverfittingRisk = (
    trainAccuracy,
    validationAccuracy
  ) => {
    if (
      trainAccuracy == null ||
      validationAccuracy == null
    ) {
      return {
        risk: "Unknown",
        gap: "N/A",
        badgeColor: "bg-gray-100 text-gray-600",
      };
    }

    const gap = Math.abs(
      (trainAccuracy - validationAccuracy) * 100
    );

    if (gap < 5) {
      return {
        risk: "Low",
        gap: gap.toFixed(1),
        badgeColor: "bg-green-100 text-green-600",
      };
    }

    if (gap < 15) {
      return {
        risk: "Medium",
        gap: gap.toFixed(1),
        badgeColor: "bg-yellow-100 text-yellow-700",
      };
    }

    return {
      risk: "High",
      gap: gap.toFixed(1),
      badgeColor: "bg-red-100 text-red-600",
    };
  };

  return {
    trainingStats,
    qualityChecks,
    lastRunTime,
    showQualityChecks,

    setTrainingStats,
    setQualityChecks,
    setLastRunTime,
    setShowQualityChecks,

    analyzeDataQuality,
    parseTrainingMetrics,
    analyzeDataset,
    calculateOverfittingRisk,
    getTimeAgo,
  };
};